import {
  BoxGeometry,
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  type BufferGeometry,
  type Material,
} from 'three';

/**
 * EN-31 Fumigadora (GDD §8.3, issue #116): a slow, chubby crop duster biplane with two tanks and boom nozzles, in
 * cream and agave green (no real insignia), and the puffs of its toxic cloud: faceted translucent spheres in a yellowish
 * green, recognisable by their shape and not only by their colour (GDD §13.6). Low-poly with flat shading. The body hitbox
 * is 1.4 x 0.5 u, 70 to 80 % of the model (about 1.9 x 0.63 u); a puff is 1.0 u across (radius 0.5).
 */
export interface CropDusterModel {
  group: Group;
  /** The pool of puffs of the cloud (a fixed number, reused). */
  puffs: Mesh[];
  dispose(): void;
}

export const CROP_DUSTER_COLORS = { body: 0xe9e2c8, wing: 0x6f9a5a, tank: 0xc9b24a, nozzle: 0x4a4f55, propeller: 0x2b2b2b, puff: 0xb7c94a } as const;
/** The cloud has a puff every 0.25 s for 4 s: 16 at most, with a margin. */
export const PUFF_POOL = 20;

export function buildCropDuster(): CropDusterModel {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number, extra: { transparent?: boolean; opacity?: number } = {}) => {
    const material = new MeshLambertMaterial({ color, flatShading: true, ...extra });
    materials.push(material);
    return material;
  };
  const part = <G extends BufferGeometry>(geometry: G, color: number, at: [number, number, number], parent: Group) => {
    geometries.push(geometry);
    const mesh = new Mesh(geometry, paint(color));
    mesh.position.set(...at);
    parent.add(mesh);
    return mesh;
  };

  const group = new Group();
  group.name = 'enemy-en31';
  const fuselage = part(new IcosahedronGeometry(0.4, 0), CROP_DUSTER_COLORS.body, [0, 0, 0], group);
  fuselage.scale.set(2, 0.9, 0.9);
  part(new BoxGeometry(1.9, 0.04, 0.8), CROP_DUSTER_COLORS.wing, [0, 0.22, 0], group);
  part(new BoxGeometry(1.9, 0.04, 0.8), CROP_DUSTER_COLORS.wing, [0, -0.12, 0], group);
  part(new CylinderGeometry(0.1, 0.1, 0.5, 6), CROP_DUSTER_COLORS.tank, [0.2, 0.3, 0], group).rotation.z = Math.PI / 2;
  part(new BoxGeometry(0.5, 0.06, 0.06), CROP_DUSTER_COLORS.nozzle, [0.7, -0.2, 0], group);
  part(new BoxGeometry(0.06, 0.4, 0.1), CROP_DUSTER_COLORS.propeller, [-0.9, 0.03, 0], group);
  group.visible = false;

  // The puffs: faceted spheres (icosahedron, detail 1) of 1.0 u across, translucent.
  const puffGeometry = new IcosahedronGeometry(0.5, 1);
  geometries.push(puffGeometry);
  const puffMaterial = paint(CROP_DUSTER_COLORS.puff, { transparent: true, opacity: 0.55 });
  const puffs = Array.from({ length: PUFF_POOL }, () => {
    const puff = new Mesh(puffGeometry, puffMaterial);
    puff.visible = false;
    return puff;
  });

  return {
    group,
    puffs,
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
